import {v5 as uuidv5} from 'uuid';
import {
  API_STATUS,
  Constants,
  SCHEDULE_DROPDOWN
} from './constants/commonConstants';
import {logger} from '@/helpers/logger';

// Check if a value is an object
export const isObject = (value) => value !== null && typeof value === 'object';

export const buildQueryParams = (params = {}) =>
  new URLSearchParams(params).toString();

export const sanitizeBlobPath = (path) => {
  if (!path) return '';
  const decoded = decodeURIComponent(path);
  if (
    decoded.includes('..') ||
    decoded.startsWith('//') ||
    decoded.includes('://')
  ) {
    throw new Error('Invalid blob path detected');
  }
  return path;
};

export const padZero = (val) => {
  return ('' + val).padStart(2, '0');
};

export const formatDateToMMDDYYYY = (dateObj) => {
  if (!dateObj) return '';
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return '';

  const mm = padZero(dateObj.getMonth() + 1);
  const dd = padZero(dateObj.getDate());
  const yyyy = dateObj.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

export function getColorTag(currentStatus, shouldApplyColor = true) {
  const errorStatuses = ['cancelled', 'danger', 'processing_failure'];
  const successStatuses = ['success', 'completed', 'approved'];

  if (shouldApplyColor) {
    if (errorStatuses.includes(currentStatus?.toLocaleLowerCase())) {
      return 'text-red-300 font-bold';
    } else if (successStatuses.includes(currentStatus?.toLocaleLowerCase())) {
      return 'text-green-300 font-bold';
    }
  } else {
    return '';
  }
}

export function getFormattedDate(
  dateString,
  timeCheck = false,
  separator = '/',
  format = 'dd-mm-yyyy'
) {
  if (!dateString || dateString == undefined) return '-';
  const dateOnly = dateString?.split('T')[0] ?? dateString;
  const date = new Date(dateOnly);
  const locale = navigator.language;

  const dateParts = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      if (part.type === 'day') acc.day = part.value;
      if (part.type === 'month') acc.month = part.value;
      if (part.type === 'year') acc.year = part.value;
      return acc;
    }, {});

  const {day = '', month = '', year = ''} = dateParts;

  const formatMapping = {
    'dd-mm-yyyy': `${day}${separator}${month}${separator}${year}`,
    'mm-dd-yyyy': `${month}${separator}${day}${separator}${year}`,
    'yyyy-dd-mm': `${year}${separator}${day}${separator}${month}`,
    'yyyy-mm-dd': `${year}${separator}${month}${separator}${day}`
  };

  let formattedDateString =
    formatMapping[format] || formatMapping['dd-mm-yyyy'];

  if (timeCheck) {
    formattedDateString += ` ${new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(date)}`;
  }
  return formattedDateString;
}

export function getLocalDateTimeDDMMYYYY(
  dateString,
  {separator = '-', hour12 = false} = {}
) {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {});

  const {
    day = '',
    month = '',
    year = '',
    hour = '',
    minute = '',
    second = ''
  } = parts;
  return `${year}${separator}${month}${separator}${day} ${hour}:${minute}:${second}`;
}

export const getSelectedRowData = (rowSelection = [], records = []) => {
  if (!Array.isArray(rowSelection)) {
    return Object.keys(rowSelection)?.length
      ? records.filter((record) => record && rowSelection[record.id])
      : [];
  }

  return rowSelection?.map((selection) =>
    selection?.index != null && records ? records[selection.index] : null
  );
};

export const getFormattedDateString = (date) => {
  if (date) {
    const formattedDate = new Date(date);
    return formattedDate.toISOString();
  } else {
    return '';
  }
};

export const parseReviewScheduleDate = (value) => {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value !== 'string') return null;

  if (value.includes('T')) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const localDateTimeRegex =
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/;
  const localDateTimeMatch = localDateTimeRegex.exec(value);
  if (localDateTimeMatch) {
    const [, y, m, day, hh, mm, ss] = localDateTimeMatch;
    const d = new Date(
      Number(y),
      Number(m) - 1,
      Number(day),
      Number(hh),
      Number(mm),
      Number(ss)
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const parseLocalDateInput = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date) {
    return Number.isNaN(dateInput.getTime()) ? null : dateInput;
  }

  if (typeof dateInput !== 'string') return null;

  // Handle ISO string with time
  if (dateInput.includes('T')) {
    const d = new Date(dateInput);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // Handle YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    const [yearStr, monthStr, dayStr] = dateInput.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  // Handle MM/DD/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateInput)) {
    const [monthStr, dayStr, yearStr] = dateInput.split('/');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  const fallback = new Date(dateInput);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

export const getBulkPointModifierStartDateUtc = ({
  schedule,
  startDate
} = {}) => {
  const scheduleVal = schedule?.toLowerCase();
  if (!scheduleVal) return '';

  const toLocalEndOfDayUtcIso = (baseDate) => {
    if (!baseDate) return '';
    const d = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate(),
      23,
      59,
      59,
      999
    );
    return d.toISOString();
  };

  const now = new Date();

  if (scheduleVal === SCHEDULE_DROPDOWN.NOW) {
    return new Date(now.getTime() + 60 * 1000).toISOString();
  }

  if (scheduleVal === SCHEDULE_DROPDOWN.END_OF_MONTH) {
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return toLocalEndOfDayUtcIso(lastDayOfMonth);
  }

  if (scheduleVal === SCHEDULE_DROPDOWN.END_OF_YEAR) {
    const lastDayOfYear = new Date(now.getFullYear(), 11, 31);
    return toLocalEndOfDayUtcIso(lastDayOfYear);
  }

  if (scheduleVal === SCHEDULE_DROPDOWN.CUSTOM_SCHEDULE) {
    const customDate = parseLocalDateInput(startDate);
    return toLocalEndOfDayUtcIso(customDate);
  }

  // Backward-compatible fallback for any older/unknown schedules
  return startDate ? getFormattedDateString(startDate) : '';
};

export const convertToLowerCase = (content) =>
  content?.toLowerCase() ?? content;

export const copyToClipboard = (value) => {
  navigator.clipboard.writeText(value).catch((err) => {
    logger.error('Failed to copy text:', err);
  });
};

export const getISOTimeStringForReview = (
  reqDate,
  isEndDate = false,
  isStartDate = false
) => {
  if (!reqDate) return;
  const date = new Date(reqDate);
  if (isStartDate) {
    date?.setHours(0, 0, 0);
  }
  if (isEndDate) {
    date?.setHours(23, 59, 59);
  }
  return date.toISOString();
};

export const getISOTimeString = (
  reqDate,
  isEndDate = false,
  isStartDate = false
) => {
  if (!reqDate) return;
  let date = new Date(reqDate);
  date = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  return convertToIsoString(date, isStartDate, isEndDate);
};

export const convertToIsoString = (date, isStartDate, isEndDate) => {
  if (isStartDate) {
    date?.setHours(0, 0, 0, 0);
  }
  if (isEndDate) {
    date?.setHours(23, 59, 59, 999);
  }
  return date.toISOString();
};

export const convertTimeStringToISODateTime = (timeString, baseDate = null) => {
  if (!timeString || typeof timeString !== 'string') return undefined;

  // If it's already an ISO string, return it
  if (timeString.includes('T') || timeString.includes('Z')) {
    return timeString;
  }

  // Parse HH:mm format
  const timeMatch = timeString.match(/^(\d{1,2}):(\d{2})$/);
  if (!timeMatch) return undefined;

  const hours = parseInt(timeMatch[1], 10);
  const minutes = parseInt(timeMatch[2], 10);

  // Use provided base date or current date
  const date = baseDate ? new Date(baseDate) : new Date();
  date.setHours(hours, minutes, 0, 0);

  return date.toISOString();
};

export const extractTimeFromISODateTime = (isoString) => {
  if (!isoString || typeof isoString !== 'string') return '';

  // If it's already in HH:mm format, return it
  if (/^(\d{1,2}):(\d{2})$/.test(isoString)) {
    return isoString;
  }

  // Extract time from ISO datetime string
  try {
    const date = new Date(isoString);
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return '';
  }
};

export const getDateFromISOString = (isoString) => {
  if (!isoString) return;

  const date = new Date(isoString);

  // Extract in local time
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const formattedDate = `${month}/${day}/${year}`;
  return formattedDate;
};

export const replaceExrtaSpaces = (input) => {
  if (typeof input !== 'string') {
    return input != null ? String(input) : '';
  }
  return input
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const getUUID = (str = 'sales-target', namespace = uuidv5.DNS) => {
  return uuidv5(str?.toUpperCase(), namespace);
};

export const formatUTCTimestamp = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  const hours = String(now.getUTCHours()).padStart(2, '0');
  const minutes = String(now.getUTCMinutes()).padStart(2, '0');
  const seconds = String(now.getUTCSeconds()).padStart(2, '0');
  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};

export const createValidationFunction = (regex, errorMessage) => (val) => {
  if (!regex.test(val)) {
    return errorMessage;
  }
};

/**
 * Checks if a value is empty, treating 0 as a valid value
 * @param {any} value - The value to check
 * @returns {boolean} - True if the value is empty, false otherwise
 */
export const isEmptyValue = (value) => {
  return !value && value !== 0;
};
/**
 * Get display currency based on country code
 * @param {string} countryCode - The country code (e.g., 'EG', 'MX')
 * @returns {string} - Currency code (e.g., 'EGP', 'MXN')
 */

// Currency map for country codes
export const currencyMap = {
  MX: 'MXN',
  SA: 'SAR',
  EG: 'EGP',
  PE: 'PEN',
  EC: 'USD',
  CO: 'COP',
  AR: 'ARS',
  DO: 'DOP',
  PL: 'PLN',
  CL: 'CLP',
  TR: 'TRY',
  RO: 'RON',
  ES: 'EUR',
  PT: 'EUR',
  RU: 'RUB',
  IN: 'INR',
  TH: 'THB',
  NZ: 'NZD'
};

// Currency map with full names for display
export const currencyMapWithNames = {
  MX: 'MXN (Mexican Peso)',
  SA: 'SAR (Saudi Riyal)',
  EG: 'EGP (Egyptian Pound)',
  PE: 'PEN (Peruvian Sol)',
  EC: 'USD (US Dollar)',
  CO: 'COP (Colombian Peso)',
  AR: 'ARS (Argentine Peso)',
  DO: 'DOP (Dominican Peso)',
  PL: 'PLN (Polish Zloty)',
  CL: 'CLP (Chilean Peso)',
  TR: 'TRY (Turkish Lira)',
  RO: 'RON (Romanian Leu)',
  ES: 'EUR (Euro)',
  PT: 'EUR (Euro)',
  RU: 'RUB (Russian Ruble)',
  IN: 'INR (Indian Rupee)',
  TH: 'THB (Thai Baht)',
  NZ: 'NZD (New Zealand Dollar)'
};

/**
 * Get currency symbol based on country code
 * @param {string} countryCode - The country code (e.g., 'EG', 'MX')
 * @returns {string} - Currency symbol (e.g., 'EGP', 'MXN')
 */
export const getCurrencySymbol = (countryCode) => {
  return currencyMap[countryCode] || '$';
};

/**
 * Get currency symbol with full name based on country code
 * @param {string} countryCode - The country code (e.g., 'EG', 'MX')
 * @returns {string} - Currency symbol with name (e.g., 'EGP (Egyptian Pound)', 'MXN (Mexican Peso)')
 */
export const getCurrencySymbolWithName = (countryCode) => {
  return currencyMapWithNames[countryCode] || '$';
};

export const isPublished = (status) => {
  return status === Constants.Published || status === API_STATUS.PUBLISHED;
};

export const isInProgress = (status) => {
  return status?.toLowerCase() === Constants.InProgress?.toLowerCase();
};

export const getQueryString = (params) => {
  const {name} = params;
  if (!name?.trim()) {
    return {success: false};
  }
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_k, v]) => _k && v != null && v !== '')
  );
  const queryString = buildQueryParams(filteredParams);
  return {success: true, queryString};
};

export const handleNameValidationViaCallbackFn = async (
  validationFn,
  queryParams
) => {
  try {
    const {queryString} = getQueryString(queryParams);
    if (queryString) {
      const response = await validationFn(queryString);
      if (response?.success === true) {
        return {success: true};
      }
    }
    return {success: false};
  } catch {
    return {success: false};
  }
};

export const flattenSegments = (groups) => {
  if (!Array.isArray(groups)) return [];
  try {
    return Array.from(
      new Set(groups.filter(Array.isArray).flat().filter(Boolean))
    );
  } catch {
    return [];
  }
};

/**
 * Capitalizes the first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} The string with the first letter capitalized
 */
export const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};
